import React, { useContext, useState, useEffect } from 'react';
import { Box, Button, Flex, Icon, Image, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { FiCalendar, FiCamera, FiCheck, FiChevronLeft, FiChevronRight, FiHelpCircle, FiImage, FiLock, FiMapPin, FiMessageSquare, FiPackage, FiUsers, FiVideo } from 'react-icons/fi';
import { Link as RouterLink, NavigateOptions, useNavigate, useParams } from 'react-router-dom';
import { Influencer, isDeliveryOnlyUgc, INFLUENCER_CANCEL_CUTOFF_HOURS } from '@foodfluence/shared';
import { CollabDataContext, CollabDataLoadingContext } from '../../../contexts/collabDataContext';
import { InfluencerCampaignDataContext, InfluencerCampaignDataLoadingContext } from '../../../contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from '../../../contexts/ClientDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import CreativeBrief from '../components/creative-brief/CreativeBrief';
import PostingDateSelector from '../components/PostingDateSelector';
import ExtensionRequestModal from '../components/ExtensionRequestModal';
import ShippingDetailsModal from '../components/ShippingDetailsModal';
import DeleteRequestModal from '../components/DeleteRequestModal';
import ConfirmActionModal, { ORDER_RECEIVED_COPY, PRODUCT_RECEIVED_COPY, shipToHomeWaitingText } from '../components/ConfirmActionModal';
import RedemptionInfoModal from '../components/RedemptionInfoModal';
import { collabApi } from '../../../services/api/collab';
import { fetchSchedulingCapacityStatus } from 'hooks/useSchedulingAvailability';
import { formatFirebaseDate, toDate, formatPostingDate, formatDateToYYYYMMDD } from '../../../utils/dateUtils';
import {
  needsExtensionRequest,
  isCampaignEnded,
  isUnconfirmedCollab,
  isCampaignFull,
  getCollabActions,
  getCollabNextStep,
  canCancelCollab,
  isClientHandledCredit,
  CollabActionDescriptor,
} from '../utils/collabUtils';
import { submissionPathForCollab } from '../utils/collabRoutes';
import { getContentDueDateRaw } from '../utils/dateUtils';
import { useDueDateTimezone } from 'hooks/useDueDateTimezone';
import {
  isShippingCampaignType,
  isPreAcceptance,
  getClientIdFromCollabRequest,
  ensureConversationExists,
  getEffectiveEndDate,
  isConfirmed,
} from 'utils/collabUtils';
import { useCollabLocation } from 'hooks/useCollabLocation';
import { getInfluencerPrimaryHandle } from '../../../utils/influencerUtils';
import ContentDueCountdown from '../components/ContentDueCountdown';
import { countdownProps } from '../utils/postByLabels';
import BrandManagedSchedulingNotice from '../components/BrandManagedSchedulingNotice';
import ShopifyShippingTracker from '../components/shopify/ShopifyShippingTracker';
import { getContentDueUIState } from '../utils/collabUtils';
import { hasRecentExtensionRequest, isInStoreCampaignType, isRetailCampaignType } from 'utils/collabUtils';
import { cancelCollabRemindersAndNotifyClient } from '../../../services/flaskService';
import { isWithinCancelCutoffOfCollab } from '../scheduling/utils/rescheduleUtils';
import CancelCollabWarningModal from '../scheduling/components/CancelCollabWarningModal';
import CancelOptionsModal from '../components/CancelOptionsModal';
import { collabProductLabel } from '../utils/collabProductLabel';
import { useProductSelectionGuard } from '../utils/useProductSelectionGuard';
import { resolveCollabProductValue } from '@foodfluence/shared';
import { CAMPAIGN_TYPE_CHIP, getDeliverableLines, showsCredit } from '../../explore/utils';
import { formatEarningsShort } from '../../../utils/earningsUtils';
import { openCrispChat } from '../../../components/FrostedSupportButton';
import { StickyCtaBar } from '../../../components/ui';
import { ff } from '../../../theme';
import { getCollabStatusCopy, STATUS_PILL_COLORS, StatusTone } from '../utils/collabDetailsStatus';

const BODY_MAX_W = { base: '560px', lg: '720px' };

/** Platform logo for a deliverable line ("2x IG Reel", "1x TikTok"); UGC gets video / camera. */
const deliverableIcon = (line: string): React.ElementType => {
  if (/tiktok/i.test(line)) return FaTiktok;
  if (/\b(ig|instagram)\b/i.test(line)) return FaInstagram;
  if (/photo/i.test(line)) return FiCamera;
  return FiVideo;
};

const StatusPill: React.FC<{ tone: StatusTone; children: React.ReactNode }> = ({ tone, children }) => (
  <Flex
    as="span"
    h="28px"
    px="10px"
    borderRadius="999px"
    display="inline-flex"
    align="center"
    fontSize="12px"
    fontWeight={600}
    lineHeight="normal"
    whiteSpace="nowrap"
    flex="none"
    {...STATUS_PILL_COLORS[tone]}
  >
    {children}
  </Flex>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="h2" m={0} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A" lineHeight="normal">
    {children}
  </Text>
);

const Fact: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <Flex as="li" align="flex-start" gap="10px" fontSize="14px" lineHeight={1.4} color="#2A2A2C">
    <Icon as={icon} boxSize="18px" flex="none" mt="1px" aria-hidden />
    <Text as="span">{children}</Text>
  </Flex>
);

type VisitRow = { icon: React.ElementType; title: string; sub?: string };

const PILL_BUTTON = {
  h: '44px',
  px: '16px',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 'normal',
  gap: '6px',
} as const;

const CollabDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: collabId } = useParams<{ id: string }>();
  const toast = useToast();

  const collabData = useContext(CollabDataContext);
  const collabDataLoading = useContext(CollabDataLoadingContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const campaignDataLoading = useContext(InfluencerCampaignDataLoadingContext);
  const clients = useContext(ClientDataContext);
  const { influencerData, loading: influencerLoading } = useContext(InfluencerDataContext) as { influencerData: Influencer | null; loading: boolean };

  const collab = collabData.find(c => c.id === collabId);
  const campaign = campaignData[collabId!];
  // A brand can add an optional product to a live campaign; the endpoint below refuses a collab
  // that owes a selection. Send the creator to make one instead of failing with a 400.
  const goToProductSelection = useProductSelectionGuard(collab, campaign);
  const client = collab && campaign ? clients[getClientIdFromCollabRequest(collab, campaign)] : undefined;
  const dueDateTimezone = useDueDateTimezone(collab, campaign);
  const isPageLoading = influencerLoading || collabDataLoading || (!!collab && campaignDataLoading);

  // Modal states
  const [isConfirmAlreadyVisitedModalOpen, setIsConfirmAlreadyVisitedModalOpen] = useState(false);
  const [isConfirmOrderReceivedModalOpen, setIsConfirmOrderReceivedModalOpen] = useState(false);
  const [isMarkReceivedModalOpen, setIsMarkReceivedModalOpen] = useState(false);
  const [isMarkingReceived, setIsMarkingReceived] = useState(false);
  const [isRedemptionInfoModalOpen, setIsRedemptionInfoModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isExtensionRequestModalOpen, setIsExtensionRequestModalOpen] = useState(false);
  const [isDeleteRequestModalOpen, setIsDeleteRequestModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [remainingSpots, setRemainingSpots] = useState<number | null>(null);
  const [schedulingFullyBooked, setSchedulingFullyBooked] = useState(false);
  const [isCancelWarningModalOpen, setIsCancelWarningModalOpen] = useState(false);
  const [isCancelOptionsModalOpen, setIsCancelOptionsModalOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const fetchRemainingSpots = async () => {
      if (campaign && campaign.maxCollabs !== null && collab?.influencerCampaignID && isUnconfirmedCollab(collab, campaign)) {
        try {
          const res = await collabApi.getAvailableSpots(collab.influencerCampaignID!, collab.id);
          setRemainingSpots(res.availableSpots);
        } catch (error) {
          console.error('Error fetching remaining spots:', error);
        }
      }
    };
    fetchRemainingSpots();

    const fetchSchedulingStatus = async () => {
      if (campaign && collab) {
        const status = await fetchSchedulingCapacityStatus(campaign, collab, collab.selectedLocation);
        setSchedulingFullyBooked(status.schedulingFullyBooked);
      }
    };
    fetchSchedulingStatus();

    if (collab?.contentRequirements && Object.keys(collab.contentRequirements).length > 0 && collab?.influencerCampaignID || campaign?.contentRequirements && Object.keys(campaign.contentRequirements).length > 0 && collab?.influencerCampaignID) {
      collabApi.downloadInspirationLinks(collab.influencerCampaignID).catch(() => {});
    }
  }, [campaign, collab]);

  const showTimePlaceBlock = !!collab && !!campaign && isConfirmed(collab, campaign) &&
    (isInStoreCampaignType(campaign?.campaignType) || isRetailCampaignType(campaign?.campaignType));

  const { location: confirmedLocation } = useCollabLocation(
    collab?.selectedLocation,
    showTimePlaceBlock && !!collab?.selectedLocation
  );

  if (isPageLoading) {
    return (
      <Box
        minH="100vh"
        bg="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="12px"
        fontFamily="display"
      >
        <Spinner size="lg" color={ff.ink} thickness="3px" />
        <Text fontSize="14px" color="#66666A">Loading collab details...</Text>
      </Box>
    );
  }

  if (!collab || !campaign) {
    return (
      <Box height="100vh" bg="white" display="flex" alignItems="center" justifyContent="center" fontFamily="display">
        <Text fontSize="15px" fontWeight={600}>Collab not found</Text>
      </Box>
    );
  }

  // --- Handlers ---

  const handleMessage = async () => {
    if (!collabId) return;
    // Pre-acceptance: never create the thread client-side (that fires the welcome early); the
    // first send creates it server-side.
    if (!isPreAcceptance(collab)) await ensureConversationExists(collabId, collab?.clientID);
    navigate(`/inbox/messages/${collabId}`, { state: { conversationId: collabId, campaign, collab, client } });
  };

  const handleDeleteRequest = async () => {
    setIsDeleting(true);
    try {
      await collabApi.deleteCollabRequest(collab.id);
      toast({
        title: 'Request Deleted',
        description: 'Your collaboration request has been deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      setIsDeleteRequestModalOpen(false);
      navigate('/collabs');
    } catch (error) {
      console.error('Error deleting collab request:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete request. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAlreadyRedeemed = async () => {
    try {
      const requireDraftApproval =
        collab?.content && Object.keys(collab.content).length > 0
          ? false
          : campaign?.requireDraftApproval;
      if (goToProductSelection()) return;
      await collabApi.advanceCollabToContentSubmission({
        collabId: collab.id,
        skipRedeem: true,
        requireDraftApproval
      });
      navigate(submissionPathForCollab(collab));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark collab as redeemed. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  // The collab context is a live Firestore listener, so the stamped due date flows back by itself.
  const handleMarkProductReceived = async () => {
    if (isMarkingReceived) return;
    setIsMarkingReceived(true);
    try {
      await collabApi.markShipToHomeReceived(collab.id);
      setIsMarkReceivedModalOpen(false);
      toast({ title: 'Content deadline started', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error && error.message ? error.message : 'Failed to confirm receipt. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsMarkingReceived(false);
    }
  };

  const handleCancelCollab = async (influencerCancelReason?: string) => {
    try {
      await collabApi.cancelCollab({ collabId: collab.id, reason: influencerCancelReason });
      cancelCollabRemindersAndNotifyClient(
        collab.id,
        client?.id ?? '',
        client?.email ?? '',
        client?.accountName ?? '',
        getInfluencerPrimaryHandle(influencerData),
        influencerData?.phoneNumber ?? ''
      );
      toast({
        title: 'Collab canceled',
        description: 'The collab has been successfully canceled.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/collabs');
    } catch (error) {
      console.error('Error canceling collab:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel the collab. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePostingDateChange = async (date: Date) => {
    try {
      const formattedDate = formatDateToYYYYMMDD(date);
      await collabApi.updateCollabScheduledPostingDate(collab.id, formattedDate);
      toast({
        title: "Date Updated",
        description: `Your expected ${isDeliveryOnlyUgc(collab, campaign) ? 'delivery' : 'posting'} date has been shared with the brand.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating posting date:', error);
      toast({
        title: "Error",
        description: error instanceof Error && error.message ? error.message : `Failed to update ${isDeliveryOnlyUgc(collab, campaign) ? 'delivery' : 'posting'} date. Please try again.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // --- Build handlers object for utils ---

  const handlers = {
    navigate: (path: string, options?: object) => navigate(path, options as NavigateOptions),
    openExtensionRequestModal: () => setIsExtensionRequestModalOpen(true),
    openDeleteRequestModal: () => setIsDeleteRequestModalOpen(true),
    openAddressModal: () => setIsAddressModalOpen(true),
    openConfirmAlreadyVisitedModal: () => setIsConfirmAlreadyVisitedModalOpen(true),
    openConfirmOrderReceivedModal: () => setIsConfirmOrderReceivedModalOpen(true),
    openMarkProductReceivedModal: () => setIsMarkReceivedModalOpen(true),
    openRedemptionInfoModal: () => setIsRedemptionInfoModalOpen(true),
    onMessage: handleMessage,
    showToast: (message: string) => toast({ description: message, status: 'info', duration: 3000, isClosable: true }),
    client,
  };

  const actions = getCollabActions(collab, campaign, influencerData, remainingSpots, handlers, schedulingFullyBooked);
  const nextStep = getCollabNextStep(collab, campaign, influencerData, remainingSpots, handlers, schedulingFullyBooked);


  // --- Status copy (hero pill, bar title, past card) ---

  const PAST_STATUSES = new Set(['collabCompleted', 'collabRejected', 'collabCanceled', 'collabExpired', 'clientRejected', 'clientCanceled']);
  const isPast = PAST_STATUSES.has(collab.status);
  const clientName = client?.accountName ?? 'the brand';
  const statusCopy = getCollabStatusCopy(collab.status, nextStep?.nextStepKey);

  const getPastMessage = (): string => {
    switch (collab.status) {
      case 'collabCompleted': return 'Great work! This collab has been completed successfully.';
      case 'collabRejected': return `You rejected this collab with ${clientName}.`;
      case 'collabCanceled': return `This collab with ${clientName} was canceled.`;
      case 'collabExpired': return `This collab with ${clientName} expired — you didn't accept within 72 hours.`;
      case 'clientRejected': return `${clientName} chose not to move forward with your request. Feel free to explore other opportunities.`;
      case 'clientCanceled': return `${clientName} canceled this collaboration.`;
      default: return `This campaign from ${clientName} has ended.`;
    }
  };
  const cancelReason = collab.status === 'clientCanceled' ? collab.clientCancelationReason : undefined;

  // Completed collabs keep their submission page around as an archive of the
  // approved content, so surface a way back into it.
  const hasSubmittedContent =
    Object.values(collab.deliverableLinks || {}).flat().length > 0 ||
    Object.keys(collab.content || {}).length > 0;
  const showViewContent = collab.status === 'collabCompleted' && hasSubmittedContent;

  // --- Money: only what the creator gets ($0 amounts are hidden) ---

  const productValue = (campaign?.productValue ?? 0) > 0 ? Math.round(resolveCollabProductValue(collab, campaign)) : 0;
  const productLabel = collabProductLabel(collab, campaign);
  const cash = collab.cash ?? 0;
  const credit = showsCredit(campaign.campaignType, collab.credit) ? collab.credit ?? 0 : 0;
  const money = [
    { k: 'Cash', v: cash },
    { k: 'Credit', v: credit },
    { k: 'Product', v: productValue },
  ].filter(m => m.v > 0).map(m => ({ k: m.k, v: formatEarningsShort(m.v) }));
  const deliverables = getDeliverableLines(campaign, collab.deliverableCounts);
  const brandHandlesCredit = isClientHandledCredit(collab, campaign);
  const retailReimbursed = isRetailCampaignType(campaign.campaignType) && productValue > 0;
  const payoutNote = cash > 0
    ? `Paid to your wallet when the collab completes · cash minus 5%${retailReimbursed ? ', plus the product cost' : ''}`
    : retailReimbursed ? 'Product cost paid to your wallet when the collab completes' : null;

  // --- Visit / store ---

  const isInStore = isInStoreCampaignType(campaign.campaignType);
  const isRetail = isRetailCampaignType(campaign.campaignType);
  const visitRows: VisitRow[] = [];
  if (showTimePlaceBlock) {
    const dateRow: VisitRow | null = (() => {
      if (isInStore && collab.scheduledCollabDate) {
        const d = toDate(collab.scheduledCollabDate);
        if (!d) return null;
        return {
          icon: FiCalendar,
          title: `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
          sub: 'Date & time',
        };
      }
      if (isRetail && collab.scheduledPostingDate) {
        return { icon: FiCalendar, title: formatPostingDate(collab.scheduledPostingDate), sub: isDeliveryOnlyUgc(collab, campaign) ? 'Delivery date' : 'Posting date' };
      }
      return null;
    })();
    const locationRow: VisitRow | null = confirmedLocation
      ? {
          icon: FiMapPin,
          title: confirmedLocation.name || `${confirmedLocation.streetNumber} ${confirmedLocation.streetName}`,
          sub: [
            confirmedLocation.name ? `${confirmedLocation.streetNumber} ${confirmedLocation.streetName}` : '',
            `${confirmedLocation.city}, ${confirmedLocation.state} ${confirmedLocation.zip}`,
          ].filter(Boolean).join(', '),
        }
      : null;
    const rows = isRetail ? [locationRow, dateRow] : [dateRow, locationRow];
    rows.forEach(r => r && visitRows.push(r));
    if (isInStore && (collab.partySize ?? 0) > 0) {
      const guests = (collab.partySize ?? 1) - 1;
      visitRows.push({ icon: FiUsers, title: `Party of ${collab.partySize}`, sub: guests > 0 ? `You + ${guests} guest${guests > 1 ? 's' : ''}` : 'Just you' });
    }
  }

  // --- Actions: primary + secondary in the bar, the rest as tiles; Message lives in the header ---

  const messageAction = actions.find(a => a.label === 'Message');
  const primaryAction: CollabActionDescriptor | undefined = nextStep?.onClick ? actions[0] : undefined;
  const otherActions = actions.filter(a => a !== primaryAction && a !== messageAction);
  const secondaryAction = otherActions[0];
  const tileActions = otherActions.slice(1);

  const showCancel = (isInStore || isRetail) && canCancelCollab(collab.status);
  const cancelDisabled = showCancel && isWithinCancelCutoffOfCollab(collab);
  const hasBar = !isPast && (!!nextStep || !!primaryAction || !!secondaryAction);

  const bannerImage = campaign?.collabImage || client?.bannerPicture || client?.instagramProfilePicture;
  const brandInitials = (client?.accountName ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
  const clientIdForLink = client?.id ?? getClientIdFromCollabRequest(collab, campaign);
  const typeChip = campaign.campaignType ? CAMPAIGN_TYPE_CHIP[campaign.campaignType] : null;
  const socials = [
    client?.instagramHandle ? { label: `IG @${client.instagramHandle}`, href: `https://instagram.com/${client.instagramHandle}` } : null,
    client?.tiktokHandle ? { label: `TikTok @${client.tiktokHandle}`, href: `https://tiktok.com/@${client.tiktokHandle}` } : null,
    client?.website ? { label: 'Website', href: client.website } : null,
  ].filter((s): s is { label: string; href: string } => !!s);

  const dueUiState = getContentDueUIState(collab, campaign);

  // --- Body (rendered above the brief) ---

  const actionsSection = (
    <VStack spacing="16px" align="stretch">
      {isPast && statusCopy && (
        <VStack
          as="section"
          aria-label="Collab status"
          align="stretch"
          spacing="12px"
          p="20px 18px"
          borderRadius="24px"
          bg={collab.status === 'collabCompleted' ? ff.limeSurface : ff.grey}
        >
          <Box><StatusPill tone={statusCopy.tone}>{statusCopy.pill}</StatusPill></Box>
          <Text textStyle="display" fontSize="32px" m={0}>{statusCopy.title}</Text>
          <Text fontSize="15px" lineHeight={1.45} color={ff.body} whiteSpace="pre-line">{getPastMessage()}</Text>
          {cancelReason && (
            <Box p="12px 14px" borderRadius="14px" bg="white" fontSize="14px" lineHeight={1.45} color="#2A2A2C">
              <Text as="strong">Reason from {clientName}:</Text> {cancelReason}
            </Box>
          )}
          {showViewContent && (
            <Button
              h="48px"
              borderRadius="999px"
              bg={ff.ink}
              color={ff.lime}
              fontSize="15px"
              fontWeight={600}
              lineHeight="normal"
              leftIcon={<Icon as={FiImage} boxSize="16px" />}
              _hover={{ bg: ff.ink }}
              _active={{ bg: '#2A2A2C' }}
              onClick={() => navigate(submissionPathForCollab(collab))}
            >
              View Content
            </Button>
          )}
        </VStack>
      )}

      {!isPast && (money.length > 0 || deliverables.length > 0) && (
        <VStack as="section" aria-label="What you get" align="stretch" spacing="12px" p="16px" border="1px solid #E4E4E0" borderRadius="20px">
          {money.length > 0 && (
            <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="8px">
              {money.map(m => (
                <VStack key={m.k} align="start" spacing="4px" minW={0}>
                  <Text textStyle="display" textTransform="none" fontSize={m.v.length > 5 ? '26px' : '32px'} letterSpacing="0" whiteSpace="nowrap">
                    {m.v}
                  </Text>
                  <Text fontSize="10px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
                    {m.k}
                  </Text>
                </VStack>
              ))}
            </Box>
          )}
          {money.length > 0 && <Box h="1px" bg={ff.line} />}
          <VStack as="ul" listStyleType="none" align="stretch" spacing="10px" m={0} p={0}>
            {deliverables.map(line => (
              <Fact key={line} icon={deliverableIcon(line)}>{line}</Fact>
            ))}
            {productValue > 0 && (
              <Fact icon={FiPackage}>{productLabel || 'Free product'} · ${productValue} value</Fact>
            )}
            {brandHandlesCredit && <Fact icon={FiCheck}>Credit handled by {clientName}</Fact>}
          </VStack>
          {payoutNote && <Text fontSize="12px" color="#66666A" lineHeight="normal">{payoutNote}</Text>}
        </VStack>
      )}

      {visitRows.length > 0 && (
        <VStack as="section" align="stretch" spacing="10px">
          <SectionLabel>{isRetail ? 'Your store' : 'Your visit'}</SectionLabel>
          <Box border="1px solid #E4E4E0" borderRadius="18px" p="4px 14px">
            {visitRows.map((r, i) => (
              <Flex key={r.title} align="flex-start" gap="12px" py="12px" borderBottom={i === visitRows.length - 1 ? undefined : `1px solid ${ff.line}`}>
                <Icon as={r.icon} boxSize="18px" flex="none" mt="2px" aria-hidden />
                <VStack as="span" align="start" spacing="2px" minW={0}>
                  <Text as="strong" fontSize="15px" lineHeight="normal">{r.title}</Text>
                  {r.sub && <Text as="span" fontSize="13px" color="#55555A" lineHeight="normal">{r.sub}</Text>}
                </VStack>
              </Flex>
            ))}
          </Box>
        </VStack>
      )}

      {(dueUiState === 'countdown' || dueUiState === 'countdownBrandManaged') && (() => {
        const dueDate = getContentDueDateRaw(collab, campaign, dueDateTimezone)!;
        return (
          <ContentDueCountdown
            dueDate={dueDate}
            brandName={dueUiState === 'countdownBrandManaged' ? client?.accountName : undefined}
            {...countdownProps(collab, campaign, dueDate, dueDateTimezone)}
          />
        );
      })()}
      {dueUiState === 'brandManagedSchedulingNotice' && <BrandManagedSchedulingNotice />}
      {dueUiState === 'shipToHomeWaiting' && (
        <Flex role="status" p="14px" borderRadius="16px" bg={ff.cream} gap="12px">
          <Icon as={FiPackage} boxSize="22px" flex="none" aria-hidden />
          <VStack as="span" align="start" spacing="4px">
            <Text as="strong" fontSize="15px" lineHeight="normal">Waiting for your product</Text>
            <Text as="span" fontSize="14px" lineHeight={1.4} color={ff.body}>{shipToHomeWaitingText(collab.contentTurnaroundDays)}</Text>
          </VStack>
        </Flex>
      )}

      {/* Posting Date Card — only during pendingSubmission */}
      {collab.status === 'pendingSubmission' && (
        <PostingDateSelector
          scheduledPostingDate={collab.scheduledPostingDate}
          onDateChange={handlePostingDateChange}
          campaign={campaign}
          collab={collab}
          usePostingDateLimits={true}
          onRequestExtension={() => setIsExtensionRequestModalOpen(true)}
          extensionRequested={hasRecentExtensionRequest(collab)}
        />
      )}

      {/* Shopify Order & Shipping — only during pendingSubmission */}
      {campaign?.campaignType === 'shopify' && collab.status === 'pendingSubmission' && (
        <VStack as="section" align="stretch" spacing="10px">
          <SectionLabel>Order &amp; shipping</SectionLabel>
          <Box p="14px" border="1px solid #E4E4E0" borderRadius="18px">
            <ShopifyShippingTracker
              clientId={campaign.clientID}
              refreshInterval={600000}
              collab={collab}
              campaign={campaign}
              label={collab.shopifyExtraShipmentOrderIDs?.length ? 'Original order' : undefined}
            />
            {collab.shopifyExtraShipmentOrderIDs?.map((orderId) => (
              <React.Fragment key={orderId}>
                <Box h="1px" bg={ff.line} my="12px" />
                <ShopifyShippingTracker
                  clientId={campaign.clientID}
                  refreshInterval={600000}
                  collab={collab}
                  campaign={campaign}
                  orderId={orderId}
                  label="Additional items"
                />
              </React.Fragment>
            ))}
          </Box>
        </VStack>
      )}

      {tileActions.length > 0 && (
        <Flex as="nav" aria-label="Collab actions" gap="8px">
          {tileActions.map(action => (
            <Flex
              as="button"
              key={action.label}
              type="button"
              aria-disabled={action.disabled || undefined}
              onClick={action.onClick}
              flex={1}
              minW={0}
              h="76px"
              boxSizing="border-box"
              borderRadius="16px"
              direction="column"
              align="center"
              justify="center"
              gap="6px"
              fontSize="12px"
              fontWeight={600}
              lineHeight="normal"
              textAlign="center"
              {...(action.disabled
                ? { border: '1px dashed #DCDCD8', color: '#6E6E72', cursor: 'not-allowed' }
                : { bg: ff.grey, color: ff.ink })}
            >
              <Icon as={action.icon} boxSize="20px" aria-hidden />
              {action.label}
            </Flex>
          ))}
        </Flex>
      )}

      {showCancel && (cancelDisabled ? (
        <VStack aria-disabled="true" alignSelf="center" minH="44px" justify="center" spacing="2px" color="#6E6E72">
          <Flex align="center" gap="6px" fontSize="14px" fontWeight={600} lineHeight="normal">
            <Icon as={FiLock} boxSize="14px" aria-hidden />Cancel collab
          </Flex>
          <Text fontSize="12px" lineHeight="normal">Locked within {INFLUENCER_CANCEL_CUTOFF_HOURS}h of your visit</Text>
        </VStack>
      ) : (
        <Button
          variant="unstyled"
          alignSelf="center"
          minH="44px"
          px="12px"
          fontSize="14px"
          fontWeight={600}
          textDecoration="underline"
          color="#A1260F"
          lineHeight="normal"
          onClick={() => setIsCancelWarningModalOpen(true)}
        >
          Cancel collab
        </Button>
      ))}
    </VStack>
  );

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} pb={hasBar ? '240px' : '40px'}>
      {/* Hero */}
      <Box position="relative" h="200px" bg={ff.grey} overflow="hidden">
        {bannerImage && <Image src={bannerImage} alt="" w="100%" h="100%" objectFit="cover" />}
        <Flex
          as="button"
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          position="absolute"
          top="12px"
          left="12px"
          w="44px"
          h="44px"
          borderRadius="999px"
          bg="white"
          color={ff.ink}
          align="center"
          justify="center"
        >
          <Icon as={FiChevronLeft} boxSize="22px" strokeWidth={2.4} aria-hidden />
        </Flex>
        {statusCopy && (
          <Box position="absolute" top="20px" right="14px">
            <StatusPill tone={statusCopy.tone}>{statusCopy.pill}</StatusPill>
          </Box>
        )}
      </Box>

      <Box maxW={BODY_MAX_W} mx="auto">
        {/* Brand + title */}
        <VStack px="20px" pt="16px" spacing="14px" align="stretch">
          <Flex align="center" gap="12px">
            <Flex flex="none" w="52px" h="52px" borderRadius="14px" bg={ff.card} align="center" justify="center" overflow="hidden" fontSize="16px" fontWeight={600}>
              {client?.instagramProfilePicture
                ? <Image src={client.instagramProfilePicture} alt="" w="100%" h="100%" objectFit="cover" />
                : brandInitials}
            </Flex>
            <VStack flex={1} minW={0} align="start" spacing="3px">
              <Flex
                as={RouterLink}
                to={`/explore/client/${clientIdForLink}`}
                minH="26px"
                align="center"
                gap="4px"
                fontSize="18px"
                fontWeight={600}
                lineHeight="normal"
                color={ff.ink}
                _hover={{ textDecoration: 'none' }}
              >
                <Text as="span" noOfLines={1}>{client?.accountName ?? ''}</Text>
                <Icon as={FiChevronRight} boxSize="16px" strokeWidth={2.4} flex="none" aria-hidden />
              </Flex>
              {socials.length > 0 && (
                <Text fontSize="13px" color="#55555A" lineHeight="normal">
                  {socials.map((s, i) => (
                    <React.Fragment key={s.label}>
                      {i > 0 && ' · '}
                      <Box as="a" href={s.href} target="_blank" rel="noopener noreferrer" color="#55555A">{s.label}</Box>
                    </React.Fragment>
                  ))}
                </Text>
              )}
            </VStack>
          </Flex>
          {campaign.name && (
            <Text as="h1" m={0} textStyle="display" fontSize="30px">{campaign.name}</Text>
          )}
          <Flex align="center" gap="8px">
            {typeChip && (
              <Flex h="28px" px="10px" borderRadius="999px" bg="#F0F0EE" color={ff.body} align="center" fontSize="12px" fontWeight={600} lineHeight="normal" flex="none">
                {typeChip}
              </Flex>
            )}
            {(collab.creatorType === 'ugc' || campaign.creatorType === 'ugc') && (
              <Flex h="28px" px="10px" borderRadius="999px" bg={ff.ink} color={ff.lime} align="center" fontSize="11px" fontWeight={600} letterSpacing="0.1em" lineHeight="normal" flex="none">
                UGC
              </Flex>
            )}
            <Box flex={1} />
            <Button
              {...PILL_BUTTON}
              bg="white"
              color={ff.ink}
              border={`1.5px solid ${ff.ink}`}
              leftIcon={<Icon as={FiHelpCircle} boxSize="16px" />}
              iconSpacing="6px"
              _hover={{ bg: ff.grey }}
              onClick={openCrispChat}
            >
              Support
            </Button>
            {messageAction && (
              <Button
                {...PILL_BUTTON}
                bg={ff.ink}
                color={ff.lime}
                leftIcon={<Icon as={FiMessageSquare} boxSize="16px" />}
                iconSpacing="6px"
                _hover={{ bg: ff.ink }}
                _active={{ bg: '#2A2A2C' }}
                onClick={messageAction.onClick}
              >
                Message
              </Button>
            )}
          </Flex>
        </VStack>

        {/* Content */}
        <Box px="20px" pt="18px">
          <CreativeBrief
            collab={collab}
            campaign={campaign}
            client={client}
            actionsSection={actionsSection}
            hideBrandHeader
          />
        </Box>
      </Box>

      {hasBar && (
        <StickyCtaBar noNav>
          <VStack align="stretch" spacing="10px" pb="12px">
            <VStack align="stretch" spacing="2px">
              {statusCopy && (
                <Text fontSize="15px" fontWeight={600} lineHeight="normal" color={statusCopy.urgent ? '#A1260F' : ff.ink}>
                  {statusCopy.title}
                </Text>
              )}
              {nextStep && (
                <Text fontSize="13px" lineHeight={1.35} color="#55555A">{nextStep.instruction}</Text>
              )}
            </VStack>
            {primaryAction && (
              <Button
                w="100%"
                h="56px"
                borderRadius="999px"
                fontSize="16px"
                fontWeight={600}
                lineHeight="normal"
                aria-disabled={primaryAction.disabled || undefined}
                leftIcon={primaryAction.disabled ? <Icon as={FiCheck} boxSize="18px" strokeWidth={2.6} /> : undefined}
                onClick={primaryAction.onClick}
                {...(primaryAction.disabled
                  ? { bg: ff.line, color: '#6E6E72', cursor: 'not-allowed', _hover: { bg: ff.line }, _active: { bg: ff.line } }
                  : { bg: ff.lime, color: ff.ink, _hover: { bg: ff.lime }, _active: { bg: '#AED923' } })}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="unstyled"
                h="44px"
                fontSize="15px"
                fontWeight={600}
                lineHeight="normal"
                textDecoration="underline"
                color={secondaryAction.disabled ? '#6E6E72' : ff.ink}
                aria-disabled={secondaryAction.disabled || undefined}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
          </VStack>
        </StickyCtaBar>
      )}

      {/* Modals */}
      <RedemptionInfoModal
        isOpen={isRedemptionInfoModalOpen}
        onClose={() => setIsRedemptionInfoModalOpen(false)}
        scheduledDate={toDate(collab.scheduledCollabDate) ?? undefined}
      />
      <ConfirmActionModal
        isOpen={isConfirmAlreadyVisitedModalOpen}
        onClose={() => setIsConfirmAlreadyVisitedModalOpen(false)}
        onConfirm={handleAlreadyRedeemed}
      />
      <ConfirmActionModal
        isOpen={isConfirmOrderReceivedModalOpen}
        onClose={() => setIsConfirmOrderReceivedModalOpen(false)}
        onConfirm={handleAlreadyRedeemed}
        {...ORDER_RECEIVED_COPY}
      />
      <ConfirmActionModal
        isOpen={isMarkReceivedModalOpen}
        onClose={() => setIsMarkReceivedModalOpen(false)}
        onConfirm={handleMarkProductReceived}
        isLoading={isMarkingReceived}
        {...PRODUCT_RECEIVED_COPY}
      />
      {isShippingCampaignType(campaign?.campaignType) && (
        <ShippingDetailsModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          collab={collab}
          campaign={campaign}
        />
      )}
      <ExtensionRequestModal
        isOpen={isExtensionRequestModalOpen}
        onClose={() => setIsExtensionRequestModalOpen(false)}
        clientName={client?.accountName ?? ''}
        clientEmail={client?.email ?? ''}
        clientId={client?.id ?? ''}
        influencerHandle={getInfluencerPrimaryHandle(influencerData)}
        influencerPhone={influencerData?.phoneNumber ?? ''}
        collabId={collab.id}
        collabStatus={collab.status}
        isCampaignFull={isCampaignFull(campaign, remainingSpots, collab)}
        endDatePassed={isCampaignEnded(campaign, collab)}
        schedulingFullyBooked={schedulingFullyBooked && !isCampaignEnded(campaign, collab)}
        collabEndDate={formatFirebaseDate(getEffectiveEndDate(campaign, collab))}
        collab={collab}
      />
      <DeleteRequestModal
        isOpen={isDeleteRequestModalOpen}
        onClose={() => setIsDeleteRequestModalOpen(false)}
        onConfirm={handleDeleteRequest}
        isLoading={isDeleting}
        clientName={client?.accountName}
      />
      <CancelCollabWarningModal
        isOpen={isCancelWarningModalOpen}
        onClose={() => setIsCancelWarningModalOpen(false)}
        onContinue={() => { setIsCancelWarningModalOpen(false); setIsCancelOptionsModalOpen(true); }}
      />
      <CancelOptionsModal
        isOpen={isCancelOptionsModalOpen}
        onClose={() => setIsCancelOptionsModalOpen(false)}
        onBack={() => setIsCancelOptionsModalOpen(false)}
        onConfirm={handleCancelCollab}
        clientName={client?.accountName ?? ''}
      />
    </Box>
  );
};

export default CollabDetailsPage;
